import { isQuotesPortal } from '@/helpers'
import { t } from '@/plugins/i18n'
import rules from '@/rules'
import { ShopAddress } from '@/types/index'
import * as i18nIsoCountries from 'i18n-iso-countries'
import countries_de from 'i18n-iso-countries/langs/de.json' with { type: 'json' }
import countries_en from 'i18n-iso-countries/langs/en.json' with { type: 'json' }
import countries_es from 'i18n-iso-countries/langs/es.json' with { type: 'json' }
import countries_fr from 'i18n-iso-countries/langs/fr.json' with { type: 'json' }
import countries_it from 'i18n-iso-countries/langs/it.json' with { type: 'json' }
import countries_nl from 'i18n-iso-countries/langs/nl.json' with { type: 'json' }
import countries_pl from 'i18n-iso-countries/langs/pl.json' with { type: 'json' }
import * as phoneUtil from 'libphonenumber-js'

i18nIsoCountries.registerLocale(countries_de)
i18nIsoCountries.registerLocale(countries_en)
i18nIsoCountries.registerLocale(countries_fr)
i18nIsoCountries.registerLocale(countries_it)
i18nIsoCountries.registerLocale(countries_pl)
i18nIsoCountries.registerLocale(countries_nl)
i18nIsoCountries.registerLocale(countries_es)

const createEmptyAddress = (): ShopAddress => {
  return {
    companyName: '',
    addition1: '',
    addition2: '',
    street: '',
    zipCode: '',
    city: '',
    postBox: '',
    iso3country: '',
    phone: '',
    email: '',
    clientNumber: '',
    url: '',
    isDefaultBillingAddress: false,
    isDefaultDeliveryAddress: false,
    canEdit: true,
    isPrimaryAddress: false,
    id: ''
  }
}

const showAddressDefaultChips = (address: ShopAddress): boolean => {
  return (
    address.isPrimaryAddress ||
    (address.isDefaultBillingAddress && !isQuotesPortal.value) ||
    (address.isDefaultDeliveryAddress && !isQuotesPortal.value)
  )
}

const getCountryName = (countryCode: string, locale: string): string | undefined => {
  // There is a false mapping in the node-i18n-iso-countries lib and we are waiting until our pull request is accepted
  // https://github.com/michaelwittig/node-i18n-iso-countries/pull/365
  const fixedCountry = countryCode == 'XKX' ? 'XKK' : countryCode
  return i18nIsoCountries.getName(fixedCountry, locale, {
    select: 'official'
  })
}

const getCountries = (locale: string): Array<{ value: string | undefined; label: string }> => {
  const list = i18nIsoCountries.getNames(locale.slice(0, 2), {
    select: 'official'
  })
  return (Object.entries(list) as Array<[string, string]>).map(([key, label]) => {
    // There is a false mapping in the node-i18n-iso-countries lib and we are waiting until our pull request is accepted
    // https://github.com/michaelwittig/node-i18n-iso-countries/pull/365
    const alpha3 = i18nIsoCountries.alpha2ToAlpha3(key)
    const fixedCountry = alpha3 === 'XKK' ? 'XKX' : alpha3
    return {
      value: fixedCountry,
      label
    }
  })
}

const getAddressFieldErrors = (address: ShopAddress): string[] => {
  const errors = []
  const fieldsToValidate = [
    { value: address.companyName, label: 'address.companyName' },
    { value: address.street, label: 'address.street' },
    { value: address.zipCode, label: 'address.zipCode' },
    { value: address.city, label: 'address.city' },
    { value: address.iso3country, label: 'address.country' }
  ]
  for (const field of fieldsToValidate) {
    if (rules.requiredRule(field.value) !== true) {
      errors.push(t('error.addressPropertyInvalid', { propertyName: t(field.label) }))
    }
  }
  return errors
}

const parsePhoneNumber = (number: string | number | undefined | null) => {
  if (typeof number === 'string' && number.startsWith('+')) {
    try {
      return phoneUtil.parsePhoneNumberWithError(number).formatInternational()
    } catch (_error) {
      return number
    }
  } else {
    return number?.toString() ?? ''
  }
}

export {
  createEmptyAddress,
  getAddressFieldErrors,
  getCountries,
  getCountryName,
  parsePhoneNumber,
  showAddressDefaultChips
}
